/**
 * Glowy AI · Audience Suggestions — /meta/kitle-onerileri.
 * Hesabın kendi verisinden (yaş/cinsiyet satış kırılımı, geçmiş ilgi set'leri, reklam metinleri) Claude'un önerdiği,
 * Meta'da doğrulanmış ve büyüklüğü ölçülmüş katmanlı kitleler. Kart başına: mevcut ad set'e uygula, ad set'i kopyalayıp
 * yeni set aç, kampanyayı kopyalayıp yeni kampanya aç. Kopyalar PAUSED açılır; ad set güncellemesi geri alınabilir.
 * Veri /api/audience-suggestions (yalnız iç ekip; backend de kilitli). Arayüz İngilizce, diğer Meta sayfalarıyla aynı stil.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle, AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, Copy, ExternalLink, Info, Layers, Loader2,
  Sparkles, Undo2, Users, Wand2, X,
} from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { useAccounts } from '../../../context/AccountContext';
import AccountSwitcher from '../../../components/AccountSwitcher';
import { apiFetch } from '../../../utils/apiFetch';

const API = 'https://api.glowytics.com/api/audience-suggestions';

// ---- formatting ----
const people = (v) => {
  const x = Number(v) || 0;
  if (x >= 1e6) return `${(x / 1e6).toLocaleString('en-US', { maximumFractionDigits: 1 })}M`;
  if (x >= 1e3) return `${Math.round(x / 1e3).toLocaleString('en-US')}K`;
  return x.toLocaleString('en-US');
};
const range = (lo, hi) => `${people(lo)}–${people(hi)}`;
const money = (v) => (v == null ? '—' : `₺${Math.round(Number(v) || 0).toLocaleString('en-US')}`);
const pct = (v, d = 0) => `${((Number(v) || 0) * 100).toLocaleString('en-US', { maximumFractionDigits: d })}%`;
const num = (v) => (Number(v) || 0).toLocaleString('en-US');
const when = (s) => (s ? new Date(s).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');
const genderLabel = (g) => (!g || !g.length || (g.includes(1) && g.includes(2)) ? 'All genders' : g[0] === 2 ? 'Women' : 'Men');
const GENDER = { female: 'Women', male: 'Men', unknown: 'Unknown' };
const ACTION = { apply_adset: 'Ad set updated', new_adset: 'New ad set', new_campaign: 'New campaign' };
const STATUS = {
  done: ['Done', 'bg-emerald-50 text-emerald-700'], error: ['Failed', 'bg-red-50 text-red-700'],
  running: ['Running', 'bg-amber-50 text-amber-700'], reverted: ['Reverted', 'bg-gray-100 text-gray-500'],
};
const WARNING = { too_wide: 'Broad: >15% of base', too_narrow: 'Narrow: <50K people', single_layer: 'Single layer' };
const adsManager = (act, kind, id) => `https://adsmanager.facebook.com/adsmanager/manage/${kind}?act=${String(act || '').replace(/^act_/, '')}&selected_${kind === 'campaigns' ? 'campaign' : 'adset'}_ids=${id}`;

async function call(path, { method = 'GET', body, brandId, accountId } = {}) {
  const qs = accountId ? `${path.includes('?') ? '&' : '?'}accountId=${encodeURIComponent(accountId)}` : '';
  const res = await apiFetch(`${API}${path}${qs}`, {
    method,
    headers: { 'Content-Type': 'application/json', 'x-brand-id': brandId },
    body: body ? JSON.stringify(body) : undefined,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) { const e = new Error(json.error || `HTTP ${res.status}`); e.status = res.status; throw e; }
  return json;
}

// ---- small pieces ----
const btn = 'inline-flex items-center gap-2 rounded-xl text-sm font-semibold px-4 py-2 whitespace-nowrap disabled:opacity-60 disabled:cursor-default transition-colors';
const btnPrimary = `${btn} bg-blue-600 hover:bg-blue-700 text-white`;
const btnSecondary = `${btn} bg-white border border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50`;
const inputCls = 'w-full px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm text-gray-800 focus:ring-2 focus:ring-blue-100 outline-none hover:border-gray-300 disabled:bg-gray-50 disabled:text-gray-400';
const labelCls = 'block text-xs font-semibold text-gray-500 mb-1.5';

function Notice({ tone = 'amber', icon: Icon = AlertTriangle, children }) {
  const cls = {
    amber: 'bg-amber-50 border-amber-200 text-amber-800', red: 'bg-red-50 border-red-200 text-red-700', blue: 'bg-blue-50 border-blue-100 text-blue-800',
  }[tone];
  return <div className={`flex items-start gap-2 border rounded-xl px-4 py-3 text-sm leading-relaxed ${cls}`}><Icon className="w-4 h-4 shrink-0 mt-0.5" /><div>{children}</div></div>;
}
function Pill({ className, children }) {
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${className}`}>{children}</span>;
}
function Stat({ label, value, sub }) {
  return (
    <div className="rounded-xl bg-gray-50 px-3 py-2.5 min-w-0">
      <div className="text-[10px] text-gray-400 uppercase tracking-wide">{label}</div>
      <div className="text-sm font-bold text-gray-900 tabular-nums truncate">{value}</div>
      {sub && <div className="text-[11px] text-gray-400 truncate">{sub}</div>}
    </div>
  );
}

// ---- base audience: age/gender purchase breakdown ----
function BaseAudience({ run }) {
  const [open, setOpen] = useState(false);
  const c = run && run.context;
  if (!c) return null;
  const b = c.base;
  const maxShare = Math.max(...(c.ageTable || []).map((r) => r.share), 0.0001);
  const ageLabel = `${b.age_min}–${b.age_max === 65 ? '65+' : b.age_max}`;
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
      <div className="p-5 grid grid-cols-2 lg:grid-cols-5 gap-3 items-stretch">
        <div className="col-span-2 lg:col-span-1 flex items-center gap-3">
          <span className="p-2 rounded-lg bg-blue-50"><Users className="w-4 h-4 text-blue-600" /></span>
          <div>
            <h2 className="text-base font-bold text-gray-900">Base audience</h2>
            <p className="text-[11px] text-gray-400">Shared by every suggestion below</p>
          </div>
        </div>
        <Stat label="Who" value={`${genderLabel(b.genders)}, ${ageLabel}`} sub={b.countries.join(', ')} />
        <Stat label="Est. size" value={c.baseReach ? range(c.baseReach.lo, c.baseReach.hi) : '—'} sub="Meta reach estimate" />
        <Stat label="Last 12 months" value={`${num(c.totals.purchases)} purchases`} sub={`${money(c.totals.spend)} spend`} />
        <Stat label="Past interest ad sets" value={num(c.interestSets.length)} sub="used as input" />
      </div>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-1.5 px-5 py-2.5 border-t border-gray-100 text-xs font-semibold text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-b-2xl">
        {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        {open ? 'Hide' : 'Show'} purchase breakdown by age and gender
      </button>
      {open && (
        <div className="border-t border-gray-100 p-5 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <div className={labelCls}>Purchases by age</div>
            <table className="w-full text-sm tabular-nums">
              <thead className="text-[11px] uppercase text-gray-400">
                <tr><th className="text-left font-semibold py-1.5">Age</th><th className="text-left font-semibold">Share of purchases</th><th className="text-right font-semibold">Purchases</th><th className="text-right font-semibold">CPA</th></tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {c.ageTable.map((r) => {
                  const lo = Number(r.age.slice(0, 2));
                  const inBase = lo >= b.age_min && lo <= b.age_max;
                  return (
                    <tr key={r.age} className={inBase ? 'text-gray-900' : 'text-gray-400'}>
                      <td className={`py-1.5 pr-2 whitespace-nowrap ${inBase ? 'font-semibold' : ''}`}>{r.age}</td>
                      <td className="w-[45%]">
                        <div className="flex items-center gap-2">
                          <div className={`h-1.5 rounded-full ${inBase ? 'bg-blue-600' : 'bg-gray-200'}`} style={{ width: `${(r.share / maxShare) * 100}%`, minWidth: 2 }} />
                          <span className="text-xs">{pct(r.share, 1)}</span>
                        </div>
                      </td>
                      <td className="text-right">{num(r.purchases)}</td>
                      <td className="text-right pl-3 whitespace-nowrap">{money(r.cpa)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div>
            <div className={labelCls}>Purchases by gender</div>
            <table className="w-full text-sm tabular-nums">
              <thead className="text-[11px] uppercase text-gray-400">
                <tr><th className="text-left font-semibold py-1.5">Gender</th><th className="text-right font-semibold">Share</th><th className="text-right font-semibold">Purchases</th><th className="text-right font-semibold">CPA</th></tr>
              </thead>
              <tbody className="divide-y divide-gray-50 text-gray-800">
                {c.genderTable.map((r) => (
                  <tr key={r.gender}><td className="py-1.5">{GENDER[r.gender] || r.gender}</td><td className="text-right">{pct(r.share, 1)}</td><td className="text-right">{num(r.purchases)}</td><td className="text-right">{money(r.cpa)}</td></tr>
                ))}
              </tbody>
            </table>
            <p className="text-xs text-gray-400 mt-4 leading-relaxed">
              How the base is set: if at least 85% of purchases come from one gender, the base targets that gender. The age range covers every age group with at least 5% of purchases and a CPA no higher than 2× the best group.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- suggestion card ----
function SuggestionCard({ s, baseHi, onApply, onDismiss }) {
  const share = baseHi ? s.reach_hi / baseHi : null;
  const notFound = (s.dropped || []).filter((d) => d.reason === 'not_found').map((d) => d.term);
  const notNarrowing = (s.dropped || []).filter((d) => d.reason === 'not_narrowing').map((d) => d.layer);
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex flex-col gap-4">
      <div className="flex items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-base font-bold text-gray-900">{s.name}</h3>
            {s.status === 'applied' && <Pill className="bg-emerald-50 text-emerald-700"><CheckCircle2 className="w-3 h-3" />Applied</Pill>}
            {(s.warnings || []).map((w) => <Pill key={w} className="bg-amber-50 text-amber-700">{WARNING[w] || w}</Pill>)}
          </div>
          <p className="text-sm text-gray-500 mt-1 leading-snug">{s.concept}</p>
        </div>
        <div className="text-right shrink-0 rounded-xl bg-blue-50 px-3 py-2">
          <div className="text-lg font-bold text-blue-700 tabular-nums leading-tight">{range(s.reach_lo, s.reach_hi)}</div>
          <div className="text-[11px] text-blue-600/80">people{share != null ? ` · ${pct(share)} of base` : ''}</div>
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        {s.layers.map((l, i) => (
          <React.Fragment key={i}>
            {i > 0 && (
              <div className="flex items-center gap-2 px-1">
                <div className="flex-1 h-px bg-gray-100" /><span className="text-[10px] font-bold tracking-widest text-blue-600">AND</span><div className="flex-1 h-px bg-gray-100" />
              </div>
            )}
            <div className="rounded-xl bg-gray-50 px-3 py-2.5">
              <div className="flex items-baseline gap-2 mb-2">
                <span className="text-xs font-semibold text-gray-700">{l.label}</span>
                <span className="text-[11px] text-gray-400 tabular-nums">{range(l.reach_lo, l.reach_hi)} alone</span>
                {l.items.length > 1 && <span className="text-[11px] text-gray-400 ml-auto">match any</span>}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {l.items.map((it) => (
                  <span key={it.id} title={`${it.type === 'behaviors' ? 'Behavior' : 'Interest'} · ID ${it.id}`} className="text-xs px-2 py-1 rounded-lg bg-white border border-gray-200 text-gray-800">
                    {it.name}{it.type === 'behaviors' && <span className="text-gray-400"> · behavior</span>}
                  </span>
                ))}
              </div>
            </div>
          </React.Fragment>
        ))}
      </div>

      <p className="text-sm text-gray-600 leading-relaxed">{s.rationale}</p>
      {(notFound.length > 0 || notNarrowing.length > 0) && (
        <p className="text-xs text-gray-400 leading-relaxed">
          {notFound.length > 0 && <>Not found on Meta, removed: {notFound.join(', ')}. </>}
          {notNarrowing.length > 0 && <>Removed because it didn't narrow the audience: {notNarrowing.join(', ')}.</>}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2 mt-auto pt-3 border-t border-gray-100">
        <button className={btnPrimary} onClick={() => onApply(s, 'apply_adset')}><Wand2 className="w-4 h-4" />Apply to ad set</button>
        <button className={btnSecondary} onClick={() => onApply(s, 'new_adset')}><Copy className="w-4 h-4" />New ad set</button>
        <button className={btnSecondary} onClick={() => onApply(s, 'new_campaign')}><Layers className="w-4 h-4" />New campaign</button>
        <button onClick={() => onDismiss(s)} title="Remove this suggestion from the list" className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-gray-400 hover:text-gray-600 px-2 py-1"><X className="w-3.5 h-3.5" />Dismiss</button>
      </div>
    </div>
  );
}

// ---- targeting summary (preview) ----
function summarize(t) {
  if (!t) return {};
  const age = t.age_range ? `${t.age_range[0]}–${t.age_range[1]} (suggestion) · control ${t.age_min || 18}–${t.age_max || 65}` : `${t.age_min || 18}–${t.age_max || 65}`;
  const layers = (t.flexible_spec || []).map((f) => Object.values(f).flat().map((x) => x.name).join(' / '));
  return {
    Location: ((t.geo_locations && (t.geo_locations.countries || [])) || []).join(', ') || ((t.geo_locations && t.geo_locations.cities) ? `${t.geo_locations.cities.length} cities` : '—'),
    Age: age,
    Gender: genderLabel(t.genders),
    'Included audiences': (t.custom_audiences || []).map((x) => x.name || x.id).join(', ') || '—',
    'Excluded audiences': (t.excluded_custom_audiences || []).map((x) => x.name || x.id).join(', ') || '—',
    'Interest layers': layers.length ? layers.join('  AND  ') : '—',
  };
}

// ---- apply dialog ----
const TITLE = { apply_adset: 'Apply to an existing ad set', new_adset: 'Create a new ad set', new_campaign: 'Create a new campaign' };
const DESCRIPTION = {
  apply_adset: "The selected ad set's targeting is replaced with this audience. Location, placements and excluded audiences stay as they are. The previous targeting is saved and can be restored from the history below.",
  new_adset: 'The selected ad set is copied with its ads into the same campaign, and the copy gets this audience. The copy is created PAUSED.',
  new_campaign: "The selected ad set's campaign is copied, the ad set is copied into it with its ads, and it gets this audience. Campaign and ad set are created PAUSED.",
};

function ApplyDialog({ brandId, accountId, structure, structureLoading, s, action, adAccountId, onClose, onDone }) {
  const [campaignId, setCampaignId] = useState('');
  const [adsetId, setAdsetId] = useState('');
  const [adsetName, setAdsetName] = useState(`${s.name} · Glowy AI`);
  const [campaignName, setCampaignName] = useState('');
  const [keepCustom, setKeepCustom] = useState(true);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const campaigns = (structure && structure.campaigns) || [];
  const campaign = campaigns.find((c) => c.id === campaignId);
  const adset = campaign && campaign.adsets.find((a) => a.id === adsetId);
  useEffect(() => { setAdsetId(''); setPreview(null); if (campaign) setCampaignName(`${campaign.name} · ${s.name}`); }, [campaignId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    setPreview(null); setError(null);
    if (!adsetId) return undefined;
    let cancelled = false;
    setPreviewLoading(true);
    call(`/${s.id}/preview`, { method: 'POST', brandId, accountId, body: { adsetId, keepCustomAudiences: keepCustom } })
      .then((j) => { if (!cancelled) setPreview(j); })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setPreviewLoading(false); });
    return () => { cancelled = true; };
  }, [adsetId, keepCustom, s.id, brandId, accountId]);

  const apply = async () => {
    setRunning(true); setError(null);
    try {
      const j = await call(`/${s.id}/apply`, { method: 'POST', brandId, accountId, body: { action, adsetId, campaignId, adsetName, campaignName, keepCustomAudiences: keepCustom } });
      setResult(j);
      onDone();
    } catch (e) { setError(e.message); } finally { setRunning(false); }
  };

  const before = preview && summarize(preview.before);
  const after = preview && summarize(preview.after);
  return (
    <div onClick={running ? undefined : onClose} className="fixed inset-0 z-[1000] bg-gray-900/40 flex items-start justify-center px-4 py-[5vh] overflow-y-auto">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-xl w-full max-w-3xl">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100">
          <span className="p-2 rounded-lg bg-blue-50">{action === 'apply_adset' ? <Wand2 className="w-4 h-4 text-blue-600" /> : action === 'new_adset' ? <Copy className="w-4 h-4 text-blue-600" /> : <Layers className="w-4 h-4 text-blue-600" />}</span>
          <div className="flex-1 min-w-0">
            <div className="text-base font-bold text-gray-900">{TITLE[action]}</div>
            <div className="text-xs text-gray-400 truncate">{s.name} · {range(s.reach_lo, s.reach_hi)} people</div>
          </div>
          <button onClick={onClose} disabled={running} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-50"><X className="w-5 h-5" /></button>
        </div>

        {result ? (
          <div className="p-5 flex flex-col gap-4">
            <div className="flex items-center gap-2 text-emerald-700 font-semibold">
              <CheckCircle2 className="w-5 h-5" />
              {action === 'apply_adset' ? 'Ad set updated' : action === 'new_adset' ? 'New ad set created (PAUSED)' : 'New campaign and ad set created (PAUSED)'}
            </div>
            <div className="flex flex-wrap gap-2">
              <a href={adsManager(adAccountId, 'adsets', result.targetAdsetId)} target="_blank" rel="noreferrer" className={btnSecondary}><ExternalLink className="w-4 h-4" />Open ad set in Ads Manager</a>
              {action === 'new_campaign' && <a href={adsManager(adAccountId, 'campaigns', result.targetCampaignId)} target="_blank" rel="noreferrer" className={btnSecondary}><ExternalLink className="w-4 h-4" />Open campaign</a>}
              <button className={btnPrimary} onClick={onClose}>Close</button>
            </div>
          </div>
        ) : (
          <div className="p-5 flex flex-col gap-4">
            <p className="text-sm text-gray-600 leading-relaxed">{DESCRIPTION[action]}</p>
            {structureLoading ? (
              <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="w-4 h-4 animate-spin" />Loading campaigns…</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label><span className={labelCls}>Campaign</span>
                  <select value={campaignId} onChange={(e) => setCampaignId(e.target.value)} className={inputCls}>
                    <option value="">Select…</option>
                    {campaigns.map((c) => <option key={c.id} value={c.id}>{c.status === 'ACTIVE' ? '● ' : '○ '}{c.name}</option>)}
                  </select>
                </label>
                <label><span className={labelCls}>{action === 'apply_adset' ? 'Ad set to update' : 'Ad set to copy (with its ads)'}</span>
                  <select value={adsetId} onChange={(e) => setAdsetId(e.target.value)} className={inputCls} disabled={!campaign}>
                    <option value="">Select…</option>
                    {(campaign ? campaign.adsets : []).map((a) => <option key={a.id} value={a.id}>{a.status === 'ACTIVE' ? '● ' : '○ '}{a.name}{a.advantage ? ' · Advantage+' : ''}</option>)}
                  </select>
                </label>
                {action === 'new_campaign' && (
                  <label><span className={labelCls}>New campaign name</span><input value={campaignName} onChange={(e) => setCampaignName(e.target.value)} className={inputCls} /></label>
                )}
                {action !== 'apply_adset' && (
                  <label><span className={labelCls}>New ad set name</span><input value={adsetName} onChange={(e) => setAdsetName(e.target.value)} className={inputCls} /></label>
                )}
              </div>
            )}
            {adset && (
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input type="checkbox" className="rounded border-gray-300 text-blue-600" checked={keepCustom} onChange={(e) => setKeepCustom(e.target.checked)} />
                Keep the ad set's included custom audiences
                <span className="text-gray-400">{adset.customAudiences.length ? `(${adset.customAudiences.slice(0, 3).join(', ')}${adset.customAudiences.length > 3 ? '…' : ''})` : '(none)'}</span>
              </label>
            )}
            {adset && keepCustom && adset.customAudiences.length > 0 && !adset.advantage && (
              <Notice>Advantage+ audience is off on this ad set, so the included custom audiences and the interest layers apply together and the audience gets much narrower.</Notice>
            )}
            {previewLoading && <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="w-4 h-4 animate-spin" />Preparing preview…</div>}
            {preview && (
              <div className="rounded-xl border border-gray-100 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                    <tr><th className="text-left px-4 py-2.5 font-semibold w-40" /><th className="text-left px-4 py-2.5 font-semibold">Current</th><th className="text-left px-4 py-2.5 font-semibold">After</th></tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {Object.keys(after).map((k) => {
                      const changed = before[k] !== after[k];
                      return (
                        <tr key={k} className="align-top">
                          <td className="px-4 py-2.5 text-gray-500 text-xs font-semibold">{k}</td>
                          <td className="px-4 py-2.5 text-gray-600 leading-snug">{before[k]}</td>
                          <td className={`px-4 py-2.5 leading-snug ${changed ? 'bg-blue-50 text-gray-900 font-semibold' : 'text-gray-400'}`}>{after[k]}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {preview && preview.advantage && (
              <Notice tone="blue" icon={Info}>Advantage+ audience is on: interests and age go to Meta as suggestions and the gender control is left as is. Meta may go beyond the suggestion when it sees fit.</Notice>
            )}
            {error && <Notice tone="red" icon={AlertCircle}>{error}</Notice>}
            <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
              {running && <span className="text-xs text-gray-400 mr-auto">Writing to Meta. If the account rate limit is hit it waits 30 s and retries, so this can take 1–3 min.</span>}
              <button className={btnSecondary} onClick={onClose} disabled={running}>Cancel</button>
              <button className={btnPrimary} onClick={apply} disabled={!preview || running || (action !== 'apply_adset' && !adsetName.trim())}>
                {running ? <Loader2 className="w-4 h-4 animate-spin" /> : action === 'apply_adset' ? <Wand2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {running ? 'Applying…' : action === 'apply_adset' ? 'Replace targeting' : 'Create as PAUSED'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---- page ----
export default function AudienceSuggestions() {
  const { selectedBrand } = useAuth();
  const { getSelectedAccountId } = useAccounts();
  const selectedAccountId = getSelectedAccountId('META_ADS');
  const accountId = selectedAccountId && selectedAccountId !== 'all' ? selectedAccountId : undefined;
  const brandId = selectedBrand?.id || '';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [dialog, setDialog] = useState(null); // { s, action }
  const [structure, setStructure] = useState(null);
  const [structureLoading, setStructureLoading] = useState(false);
  const [reverting, setReverting] = useState(null);

  const load = useCallback(async () => {
    if (!brandId) return;
    setLoading(true); setError(null);
    try { const j = await call('', { brandId, accountId }); setData(j); setGenerating(!!j.generating); } catch (e) { setError(e.message); setData(null); } finally { setLoading(false); }
  }, [brandId, accountId]);
  useEffect(() => { setStructure(null); load(); }, [load]);

  // poll while a generation started elsewhere is still running
  useEffect(() => {
    if (!data || !data.generating) return undefined;
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [data, load]);

  const generate = async () => {
    setGenerating(true); setError(null);
    try { await call('/generate', { method: 'POST', brandId, accountId }); await load(); } catch (e) { setError(e.status === 409 ? 'A generation is already running for this brand.' : e.message); } finally { setGenerating(false); }
  };
  const openDialog = async (s, action) => {
    setDialog({ s, action });
    if (!structure && !structureLoading) {
      setStructureLoading(true);
      try { setStructure(await call('/structure', { brandId, accountId })); } catch (e) { setError(e.message); setDialog(null); } finally { setStructureLoading(false); }
    }
  };
  const dismiss = async (s) => {
    try { await call(`/${s.id}/dismiss`, { method: 'POST', brandId, accountId }); setData((v) => ({ ...v, suggestions: v.suggestions.filter((x) => x.id !== s.id) })); } catch (e) { setError(e.message); }
  };
  const revert = async (a) => {
    if (!window.confirm(`Revert "${a.suggestion_name}"? The ad set's previous targeting will be written back to Meta.`)) return;
    setReverting(a.id); setError(null);
    try { await call(`/applications/${a.id}/revert`, { method: 'POST', brandId, accountId }); await load(); } catch (e) { setError(e.message); } finally { setReverting(null); }
  };

  const run = data && data.run;
  const baseHi = run && run.context && run.context.baseReach && run.context.baseReach.hi;
  const suggestions = useMemo(() => (data && data.suggestions) || [], [data]);
  const history = (data && data.applications) || [];

  return (
    <div className="min-h-screen p-4 space-y-6 max-w-screen-2xl mx-auto w-full overflow-x-hidden bg-[#f6f8ff]/70">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center bg-white p-5 rounded-2xl shadow-sm border border-gray-100 gap-4">
        <div className="flex items-center gap-4 min-w-0">
          <div className="p-3 bg-blue-50 border border-blue-100 shadow-sm rounded-xl shrink-0">
            <img src="/6033716.png" alt="Meta Ads" className="w-8 h-8 object-contain" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold text-gray-900 leading-tight">Audience Suggestions</h1>
              <Pill className="bg-blue-50 text-blue-700"><Sparkles className="w-3 h-3" />Glowy AI</Pill>
            </div>
            <p className="text-sm text-gray-500 mt-1">
              Layered interest audiences built from this account's own data, verified and sized on Meta
              {run ? ` · last run ${when(run.created_at)}${run.created_by ? ` by ${run.created_by}` : ''}` : ''}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
          <AccountSwitcher platform="META_ADS" />
          <button onClick={generate} disabled={generating || !brandId} className={btnPrimary}>
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {generating ? 'Generating… (1–2 min)' : run ? 'Regenerate' : 'Generate suggestions'}
          </button>
        </div>
      </div>

      {error && <Notice tone="red" icon={AlertCircle}>{error}</Notice>}
      {data?.lastRun?.status === 'error' && !generating && <Notice>Last generation failed ({when(data.lastRun.created_at)}): {data.lastRun.error}</Notice>}
      {loading && !data && (
        <div className="flex items-center gap-2 text-gray-500 text-sm py-10 justify-center"><Loader2 className="w-5 h-5 animate-spin" />Loading…</div>
      )}

      {data && !run && !generating && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-6 py-12 text-center">
          <span className="inline-flex p-3 rounded-xl bg-blue-50 mb-3"><Sparkles className="w-6 h-6 text-blue-600" /></span>
          <h2 className="text-lg font-bold text-gray-900">No suggestions for this brand yet</h2>
          <p className="text-sm text-gray-500 mt-2 mb-5 max-w-xl mx-auto leading-relaxed">
            We read the account's last 12 months of purchases by age and gender, its past interest ad sets and its active ad copy. Every suggested interest is checked on Meta and every audience is sized. Generating doesn't change anything in the ad account.
          </p>
          <button className={btnPrimary} onClick={generate}><Sparkles className="w-4 h-4" />Generate suggestions</button>
        </div>
      )}
      {generating && (
        <div className="flex items-center gap-3 bg-white rounded-2xl border border-gray-100 shadow-sm px-5 py-4 text-sm text-gray-600">
          <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
          Reading account data, drafting audiences and sizing them on Meta. This takes 1–2 minutes.
        </div>
      )}

      {run && <BaseAudience run={run} />}

      {suggestions.length > 0 && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {suggestions.map((s) => <SuggestionCard key={s.id} s={s} baseHi={baseHi} onApply={openDialog} onDismiss={dismiss} />)}
        </div>
      )}
      {run && !suggestions.length && !generating && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-5 py-4 text-sm text-gray-500">All suggestions from this run were dismissed. Click "Regenerate" for new ones.</div>
      )}

      {history.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <h2 className="text-base font-bold text-gray-900">Application history</h2>
            <p className="text-[11px] text-gray-400">Every change made from this page. Ad set updates can be reverted.</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                <tr>{['Time', 'Suggestion', 'Action', 'Target', 'Status', 'By', ''].map((h) => <th key={h} className="text-left px-4 py-3 font-semibold whitespace-nowrap">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {history.map((a) => {
                  const [label, cls] = STATUS[a.status] || [a.status, 'bg-gray-100 text-gray-500'];
                  return (
                    <tr key={a.id} className="hover:bg-gray-50 align-top">
                      <td className="px-4 py-2.5 whitespace-nowrap tabular-nums text-gray-600">{when(a.created_at)}</td>
                      <td className="px-4 py-2.5 text-gray-900">{a.suggestion_name || `#${a.suggestion_id}`}</td>
                      <td className="px-4 py-2.5 whitespace-nowrap text-gray-600">{ACTION[a.action] || a.action}</td>
                      <td className="px-4 py-2.5 whitespace-nowrap">
                        {a.target_adset_id
                          ? <a href={adsManager(data.adAccountId, 'adsets', a.target_adset_id)} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">Ad set {a.target_adset_id}</a>
                          : <span className="text-gray-400">Source {a.source_adset_id}</span>}
                      </td>
                      <td className="px-4 py-2.5">
                        <Pill className={cls}>{label}</Pill>
                        {a.error && <div className="text-xs text-red-600 mt-1 max-w-sm leading-snug">{a.error}</div>}
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap text-gray-400">{a.created_by}</td>
                      <td className="px-4 py-2.5 text-right">
                        {a.action === 'apply_adset' && a.status === 'done' && (
                          <button className={`${btnSecondary} !px-3 !py-1 !text-xs`} onClick={() => revert(a)} disabled={reverting === a.id}>
                            {reverting === a.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Undo2 className="w-3.5 h-3.5" />}{reverting === a.id ? 'Reverting…' : 'Revert'}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {data && (
        <p className="text-xs text-gray-400 text-center">Source: Meta Marketing API (live) · Account {data.adAccountId} · copies are always created PAUSED</p>
      )}

      {dialog && (
        <ApplyDialog
          brandId={brandId} accountId={accountId} structure={structure} structureLoading={structureLoading} s={dialog.s} action={dialog.action} adAccountId={data?.adAccountId}
          onClose={() => setDialog(null)} onDone={load}
        />
      )}
    </div>
  );
}
